// src/features/apps/components/AppReadinessCard.tsx

import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBuildingStore,
  IconCircleCheck,
  IconClock,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import { FACTORY_STORE_INFO, type FactoryStoreKey } from '@/lib/constants/appFactory';
import { BUILD_READINESS_COPY, type BuildReadinessFixKind } from '@/lib/constants/buildReadiness';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryStoreAccount } from '@/lib/types/AppFactory';
import type { AppLegalVersionSummary } from '@/lib/types/AppLegal';
import { AppFactoryUtils } from '../AppFactoryUtils';
import type { BuildReadinessRow, BuildReadinessTone, BuildReadinessView } from '../BuildReadinessUtils';
import { PrivacyTemplate } from '../legal/PrivacyTemplate';
import { StoreAccountUtils } from '../StoreAccountUtils';
import type { StoreAppCheckLine, StoreAppCheckTone } from '../StoreAppCheckUtils';
import type { WizardStep } from './AppRokuSetupWizard';

interface AppReadinessCardProps {
  app: AppCfgsDto;
  /** The stores to chip; default = every store the app onboards. */
  stores?: FactoryStoreKey[];
  storeAccounts: FactoryStoreAccount[];
  legal: AppLegalVersionSummary | null;
  /** Absent = the reads failed (the card still renders what it can). */
  unavailable?: boolean;
  /**
   * The farm's verdict on the saved store app id (ADR-0029), shown under that
   * store's account; null / absent = no line (no check, no id, no answer).
   */
  storeAppCheck?: { store: FactoryStoreKey; line: StoreAppCheckLine } | null;
  /**
   * The "To build" group (build readiness, 3.34.5: BuildReadinessUtils.view);
   * null = unknown (loading, an older api or daemon, a failed read) — the group
   * shows nothing new.
   */
  build: BuildReadinessView | null;
  onOpenWizard: (step: WizardStep) => void;
  /** A "To build" row's fix button: where the item is fixed. */
  onFix: (fix: BuildReadinessFixKind) => void;
}

/**
 * The Status tab's "Readiness" card. Two groups: **To build** (3.34.5) — one
 * row per prerequisite the factory would refuse a build for, in the words the
 * refusal would use, each with a button to where it is fixed (or the support
 * contact in its sentence), one green line when everything holds; **To submit**
 * (B1) — one chip per store for the developer account behind the app, one for
 * the privacy policy, each opening its wizard step. Reads only; the factory's
 * admission, the daemon's ledger and the shard are the truth.
 */
export function AppReadinessCard({
  app,
  stores = StoreAccountUtils.storesForApp(app),
  storeAccounts,
  legal,
  unavailable,
  storeAppCheck,
  build,
  onOpenWizard,
  onFix,
}: AppReadinessCardProps) {
  const { selectedCId, selectedCustomer } = useAuth();
  const { operatorName } = useHelpTokens();
  const parentCIds = selectedCustomer?.cfg.parentCId ? [selectedCustomer.cfg.parentCId] : [];
  // A hand-set (or pre-generator) listing URL is a policy the customer hosts:
  // "Custom URL", never "Missing" (the listing is what the stores see).
  const privacy = PrivacyTemplate.readiness(
    app.listing?.privacyPolicyUrl,
    legal,
    PrivacyTemplate.derivedFacts(app),
  );

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Text fw={600}>{BUILD_READINESS_COPY.title}</Text>

        {/* To build: the factory's admission, before the click. Unknown = nothing new. */}
        {build && (
          <Stack gap={6}>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase">
              {BUILD_READINESS_COPY.toBuild}
            </Text>
            {build.ready && (
              <Group gap="xs" wrap="nowrap">
                <IconCircleCheck size={16} color={TONE_COLOR.green} />
                <Text size="sm" style={{ color: TONE_COLOR.green }}>
                  {BUILD_READINESS_COPY.ready}
                </Text>
              </Group>
            )}
            {build.rows.map((row) => (
              <BuildReadinessRowView key={row.reason} row={row} onFix={onFix} />
            ))}
          </Stack>
        )}

        {/* To submit: the developer account per store + the privacy policy (B1) */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          {BUILD_READINESS_COPY.toSubmit}
        </Text>
        {unavailable && (
          <Text size="xs" c="dimmed">
            Store account status is currently unavailable; the chips below may be stale.
          </Text>
        )}
        {stores.map((store) => {
          const info = FACTORY_STORE_INFO[store];
          const chip = StoreAccountUtils.readinessChip(
            StoreAccountUtils.resolve(storeAccounts, selectedCId ?? 0, parentCIds, store),
            operatorName,
          );
          return (
            <Stack key={store} gap={4}>
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap">
                  <IconBuildingStore size={16} />
                  <Text size="sm">{info.label} developer account</Text>
                  <Badge variant="light" color={chip.color} size="sm">
                    {chip.label}
                  </Badge>
                </Group>
                <Button size="xs" variant="subtle" onClick={() => onOpenWizard('store')}>
                  {chip.account ? 'Open' : 'Set up'}
                </Button>
              </Group>
              {storeAppCheck?.store === store && <StoreAppCheckRow line={storeAppCheck.line} />}
            </Stack>
          );
        })}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <IconShieldCheck size={16} />
            <Text size="sm">Privacy policy</Text>
            <Badge variant="light" color={privacy.color} size="sm">
              {privacy.label}
            </Badge>
          </Group>
          <Button size="xs" variant="subtle" onClick={() => onOpenWizard('privacy')}>
            {privacy.action}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ── Internal: one "To build" row (3.34.5) ──

const TONE_COLOR = {
  green: 'var(--mantine-color-green-7)',
  red: 'var(--mantine-color-red-7)',
  orange: 'var(--mantine-color-orange-7)',
  neutral: 'var(--mantine-color-dimmed)',
} as const;

const BUILD_TONE_COLOR: Record<BuildReadinessTone, string> = {
  red: TONE_COLOR.red,
  neutral: TONE_COLOR.neutral,
};

function BuildReadinessRowView({
  row,
  onFix,
}: {
  row: BuildReadinessRow;
  onFix: (fix: BuildReadinessFixKind) => void;
}) {
  const color = BUILD_TONE_COLOR[row.tone];
  const Icon = row.tone === 'neutral' ? IconClock : IconAlertTriangle;
  const fixLabel = row.fix ? BUILD_READINESS_COPY.fixLabels[row.fix] : undefined;
  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <Icon size={16} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <Text size="sm">{row.label}</Text>
          <Text size="xs" style={{ color }}>
            {row.sentence}
          </Text>
        </div>
      </Group>
      {row.fix && fixLabel && (
        <Button size="xs" variant="subtle" style={{ flexShrink: 0 }} onClick={() => onFix(row.fix!)}>
          {fixLabel}
        </Button>
      )}
    </Group>
  );
}

// ── Internal: the store app id check line (ADR-0029) ──

const STORE_APP_CHECK_TONE_COLOR: Record<StoreAppCheckTone, string> = {
  green: TONE_COLOR.green,
  red: TONE_COLOR.red,
  orange: TONE_COLOR.orange,
  neutral: TONE_COLOR.neutral,
};

function StoreAppCheckRow({ line }: { line: StoreAppCheckLine }) {
  const color = STORE_APP_CHECK_TONE_COLOR[line.tone];
  const Icon =
    line.tone === 'green' ? IconCircleCheck : line.tone === 'neutral' ? IconClock : IconAlertTriangle;
  return (
    // aligned under the account's label (icon 16 + gap)
    <Group gap={6} wrap="nowrap" align="flex-start" pl={26}>
      <Icon size={14} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
      <Text size="xs" style={{ color }}>
        {line.text}
        {line.when && (
          <Text span size="xs" c="dimmed">
            {' · '}
            {AppFactoryUtils.formatWhen(line.when)}
          </Text>
        )}
      </Text>
    </Group>
  );
}
